/* eslint-disable @typescript-eslint/no-explicit-any */
import Image from "next/image";

import bullet from "@/assets/img/how-it-works/bullet.svg";
import line from "@/assets/img/how-it-works/line_divider.svg";
import { NextMuiLink } from "@/components/Link/NextMuiLink";
import { sanitizeText } from "@/utils/string";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { Document, MARKS } from "@contentful/rich-text-types";
import { Box, Button, darken, Stack, Typography } from "@mui/material";

import { styles } from "./styles";
import { PartnerInfoCardProps } from "./types";

export const PartnerInfoCard: React.FC<PartnerInfoCardProps> = ({
  name,
  title,
  description,
  image,
  logo,
  url,
  buttonTitle,
  backgroundColor,
  textColor,
  buttonColor
}) => {
  return (
    <Stack
      direction={{ xs: "column-reverse", sm: "column-reverse", md: "row" }}
      position="relative"
      height="100%"
      css={styles.stack(backgroundColor)}
    >
      <Stack direction="column" flex={1}>
        <Box bgcolor="#ffffff">
          <Stack display="flex">
            <Stack justifySelf="center" textAlign="start" alignItems="start" height="auto">
              {logo ? <Image src={logo} alt="" width={200} height={110} css={styles.logo} /> : null}
            </Stack>
          </Stack>
          <Box position="relative" lineHeight={0.5}>
            <Image src={line} alt="" style={{ width: "100%", height: "auto", top: "-3px", position: "absolute" }} />
            <Image src={bullet} alt="" css={styles.bullet} />
          </Box>
        </Box>

        <Box bgcolor={backgroundColor}>
          <Stack flex={1} mt={{ xs: 4, sm: 4, md: 4, lg: 5 }} ml={{ xs: 3, sm: 3, md: 2, lg: 10 }} mr={2}>
            <Typography
              color={textColor || "#0045B5"}
              fontSize={32}
              fontWeight={600}
              style={{ wordBreak: "break-word" }}
              lineHeight="40px"
              whiteSpace="pre-line"
            >
              {title}
            </Typography>

            <Typography
              component="div"
              color={textColor || "#0045B5"}
              fontSize={19}
              marginTop={3}
              lineHeight="32px"
              style={{ wordBreak: "break-word" }}
            >
              {typeof description === "object"
                ? documentToReactComponents(description as Document, {
                    renderMark: {
                      [MARKS.BOLD]: (text) => <span style={{ fontWeight: 600 }}>{text}</span>
                    }
                  })
                : description}
            </Typography>

            <Button
              component={NextMuiLink}
              href={url}
              target={url ? "_blank" : "_self"}
              onClick={() => {
                try {
                  const sanitizedPartnerName = sanitizeText(name || "");
                  const sanitizedButtonTitle = sanitizeText(buttonTitle || "");
                  if ((window as any).dataLayer) {
                    (window as any).dataLayer.push({
                      event: "hero_interaction",
                      content_type: `${sanitizedPartnerName}:${buttonTitle ? sanitizedButtonTitle : "saiba_mais"}`
                    });
                    console.log(`Evento disparado: parceiro:${sanitizedPartnerName}`);
                  }
                } catch (error) {
                  console.error("Erro ao enviar o evento para o Google Tag Manager", error);
                }
              }}
              sx={{
                backgroundColor: buttonColor,
                "&:hover": {
                  backgroundColor: darken(buttonColor, 0.2)
                }
              }}
              css={styles.box__button}
            >
              {buttonTitle || "Saiba mais"}
            </Button>
          </Stack>
        </Box>
      </Stack>

      <Box
        position="relative"
        minHeight="400px"
        minWidth="54vw"
        maxHeight="780px"
        style={{
          border: "3px solid",
          borderLeftColor: "#0045B5",
          borderBottomColor: "#0045B5",
          borderTop: 0,
          borderRight: 0,
          overflow: "hidden"
        }}
        css={styles.image}
      >
        <Box
          component="img"
          src={image}
          alt=""
          position="absolute"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover"
          }}
        />
      </Box>
    </Stack>
  );
};
